// ============================================================
// PnLTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Monthly view (default) or a free date range
//   • Revenue → COGS → gross → expenses → net
//   • Expense split by category
//   • What the module is owed, owes, and holds in stock
//
// MONTHLY FIRST
// HR posts salaries monthly and scrap is reported monthly, so a
// calendar month is the period the owner already thinks in. It
// also makes the comparison possible: one month's figures mean
// little without last month's beside them. Custom range is
// there for the odd question a month can't answer.
//
// WHAT THE NUMBERS ARE
//   Revenue   invoiced in the period, not collected. Cash
//             timing lives in receivables — otherwise a
//             slow-paying month reads as an unprofitable one.
//   COGS      the frozen cost of exactly what was invoiced,
//             traced from the jumbo roll it came off. Unsold
//             stock is not in here; it sits in the stock value
//             below.
//   Scrap     pure income. Waste absorbs no cost upstream, so
//             there is nothing to net against it.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, TrendingUp, TrendingDown, Calendar, CalendarRange,
} from 'lucide-react';
import { DEFAULT_FROM_DATE, todayISO } from '../components/WindowModal';
import PrintButton from '../print/PrintButton';
import { pnlReportConfig } from './reports/pnlReport.config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString('en-PK', { maximumFractionDigits: 0 });
  return v < 0 ? `(Rs ${s})` : `Rs ${s}`;
};
const kg = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 2 })} kg`;

// First and last day of a YYYY-MM key, in local terms. Building
// the string by hand avoids the UTC shift that toISOString()
// causes in PKT — which would silently pull the 1st into the
// previous month.
const monthBounds = (key) => {
  const [y, m] = key.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const pad = (n) => String(n).padStart(2, '0');
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` };
};

const monthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1)
    .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// Last 24 months, newest first
const buildMonthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = monthKey(d);
    if (key < DEFAULT_FROM_DATE.slice(0, 7)) break;
    out.push({ key, label: monthLabel(key) });
  }
  return out;
};

const prevMonthKey = (key) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return monthKey(d);
};


// ============================================================
// SHARED UI
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

// Segmented control for Monthly / Custom range
const ModeToggle = ({ mode, setMode }) => (
  <div style={{
    display: 'inline-flex', padding: '3px', borderRadius: '9px',
    background: C.borderSoft, border: `1px solid ${C.border}`,
  }}>
    {[
      ['monthly', 'Monthly', Calendar],
      ['range',   'Custom range', CalendarRange],
    ].map(([key, label, Icon]) => {
      const active = mode === key;
      return (
        <button
          key={key}
          type="button"
          onClick={() => setMode(key)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '7px 14px', borderRadius: '7px', border: 'none',
            background: active ? '#ffffff' : 'transparent',
            color: active ? C.text : C.textSoft,
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            fontFamily: 'inherit', whiteSpace: 'nowrap',
            boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
          }}
        >
          <Icon size={14} /> {label}
        </button>
      );
    })}
  </div>
);


// ============================================================
// P&L LINE
//
// Sub-lines are indented and muted; the three result lines
// (gross, net, and the revenue subtotal) carry a rule above
// them so the arithmetic reads down the page.
// ============================================================

const Line = ({ label, value, sub, bold, rule, note, tone }) => {
  const colour =
    tone === 'good' ? C.success :
    tone === 'bad'  ? C.danger  : C.text;

  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: '14px',
      padding: sub ? '7px 0' : '10px 0',
      borderTop: rule ? `1px solid ${C.border}` : 'none',
      marginTop: rule ? '6px' : 0,
      paddingLeft: sub ? '18px' : 0,
    }}>
      <span style={{
        flex: 1,
        fontSize: sub ? '13px' : '14px',
        fontWeight: bold ? '700' : (sub ? '400' : '600'),
        color: sub ? C.textSoft : C.text,
      }}>
        {label}
        {note && (
          <span style={{ fontSize: '11px', color: C.textMuted, marginLeft: '8px', fontWeight: '400' }}>
            {note}
          </span>
        )}
      </span>
      <span style={{
        fontSize: bold ? '19px' : (sub ? '13px' : '15px'),
        fontWeight: bold ? '700' : '600',
        color: bold ? colour : (sub ? C.textSoft : C.text),
        whiteSpace: 'nowrap',
      }}>
        {money(value)}
      </span>
    </div>
  );
};


// ============================================================
// COMPARISON PILL
// ============================================================

const Delta = ({ current, previous, label }) => {
  if (previous == null) return null;

  const cur  = Number(current);
  const prev = Number(previous);
  const diff = cur - prev;

  if (Math.abs(diff) < 1) {
    return (
      <span style={{ fontSize: '12px', color: C.textMuted }}>
        level with {label}
      </span>
    );
  }

  const up   = diff > 0;
  const Icon = up ? TrendingUp : TrendingDown;

  // More profit is good; a bigger loss is not. Comparing against
  // a negative previous figure makes percentages meaningless, so
  // only the rupee change is shown in that case.
  const pct = prev !== 0 && prev > 0
    ? Math.round(Math.abs(diff / prev) * 100)
    : null;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '12px', fontWeight: '600',
      color: up ? C.success : C.danger,
    }}>
      <Icon size={13} />
      {money(Math.abs(diff))}
      {pct != null && ` (${pct}%)`}
      <span style={{ color: C.textMuted, fontWeight: '400' }}>
        vs {label}
      </span>
    </span>
  );
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const PnLTab = () => {
  const [mode, setMode] = useState('monthly');

  const monthOptions = buildMonthOptions();
  const [month, setMonth] = useState(monthKey(new Date()));

  const [range, setRange] = useState({
    from_date: DEFAULT_FROM_DATE,
    to_date:   todayISO(),
  });

  const [pnl, setPnl]           = useState(null);
  const [prevPnl, setPrevPnl]   = useState(null);
  const [trend, setTrend]       = useState([]);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [banner, setBanner]     = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const period = mode === 'monthly'
    ? (() => { const b = monthBounds(month); return { from_date: b.from, to_date: b.to }; })()
    : range;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = (p) =>
        `from_date=${p.from_date}&to_date=${p.to_date}`;

      const calls = [
        axios.get(`${API}/tape/finance/pnl?${q(period)}`, { headers }),
        axios.get(`${API}/tape/finance/balances`, { headers }),
      ];

      // The previous month only matters in monthly mode — a
      // custom range has no natural "one before".
      if (mode === 'monthly') {
        const pb = monthBounds(prevMonthKey(month));
        calls.push(
          axios.get(`${API}/tape/finance/pnl?from_date=${pb.from}&to_date=${pb.to}`, { headers })
        );
      } else {
        calls.push(
          axios.get(`${API}/tape/finance/trend?${q(period)}`, { headers })
        );
      }

      const [p, b, extra] = await Promise.all(calls);

      setPnl(p.data.data);
      setBalances(b.data.data);

      if (mode === 'monthly') {
        setPrevPnl(extra.data.data);
        setTrend([]);
      } else {
        setPrevPnl(null);
        setTrend(extra.data.data || []);
      }
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not load the P&L');
    } finally {
      setLoading(false);
    }
  }, [mode, month, range.from_date, range.to_date]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const r = pnl?.revenue   || {};
  const c = pnl?.cogs      || {};
  const e = pnl?.expenses  || { by_category: [], total: 0 };

  const netTone   = Number(pnl?.net_profit || 0) >= 0 ? 'good' : 'bad';
  const grossTone = Number(pnl?.gross_profit || 0) >= 0 ? 'good' : 'bad';

  const periodLabel = mode === 'monthly'
    ? monthLabel(month)
    : `${new Date(range.from_date).toLocaleDateString('en-GB')} – ${new Date(range.to_date).toLocaleDateString('en-GB')}`;

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Period picker */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', padding: '18px 22px', marginBottom: '16px',
        display: 'flex', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
            Profit &amp; loss
          </h3>
          <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
            {periodLabel}
          </p>
        </div>

        <PrintButton
          label="Print P&L"
          filename={`PnL-${period.from_date}-to-${period.to_date}`}
          disabled={loading || !pnl}
          getConfig={() =>
            pnlReportConfig({
              pnl, prevPnl, trend, balances, mode, periodLabel, period,
            })
          }
        />

        <ModeToggle mode={mode} setMode={setMode} />

        {mode === 'monthly' ? (
          <div style={{ width: '190px' }}>
            <Field label="Month">
              <select
                style={inputStyle}
                value={month}
                onChange={ev => setMonth(ev.target.value)}
              >
                {monthOptions.map(m => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </Field>
          </div>
        ) : (
          <>
            <div style={{ width: '150px' }}>
              <Field label="From">
                <TextInput
                  type="date"
                  value={range.from_date}
                  onChange={ev => setRange({ ...range, from_date: ev.target.value })}
                />
              </Field>
            </div>
            <div style={{ width: '150px' }}>
              <Field label="To">
                <TextInput
                  type="date"
                  value={range.to_date}
                  onChange={ev => setRange({ ...range, to_date: ev.target.value })}
                />
              </Field>
            </div>
          </>
        )}
      </div>

      {loading && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: '12px', padding: '48px', textAlign: 'center',
          color: C.textMuted, fontSize: '14px',
        }}>
          Loading…
        </div>
      )}

      {!loading && pnl && (
        <>
          {/* Headline */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '14px', marginBottom: '16px',
          }}>
            {[
              {
                label: 'Revenue',
                value: money(r.total),
                foot: <Delta current={r.total} previous={prevPnl?.revenue?.total} label="last month" />,
              },
              {
                label: 'Gross profit',
                value: money(pnl.gross_profit),
                tone: grossTone,
                foot: pnl.gross_margin_pct != null
                  ? <span style={{ fontSize: '12px', color: C.textMuted }}>
                      {pnl.gross_margin_pct}% margin
                    </span>
                  : null,
              },
              {
                label: 'Expenses',
                value: money(e.total),
                foot: <Delta current={e.total} previous={prevPnl?.expenses?.total} label="last month" />,
              },
              {
                label: 'Net profit',
                value: money(pnl.net_profit),
                tone: netTone,
                foot: <Delta current={pnl.net_profit} previous={prevPnl?.net_profit} label="last month" />,
              },
            ].map(card => (
              <div key={card.label} style={{
                background: C.card, border: `1px solid ${C.border}`,
                borderRadius: '10px', padding: '16px 18px',
              }}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {card.label}
                </div>
                <div style={{
                  fontSize: '23px', fontWeight: '700', marginTop: '4px',
                  color: card.tone === 'good' ? C.success
                       : card.tone === 'bad'  ? C.danger
                       : C.text,
                }}>
                  {card.value}
                </div>
                <div style={{ marginTop: '6px', minHeight: '17px' }}>{card.foot}</div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '16px',
          }}>

            {/* The statement */}
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: '12px', padding: '20px 24px',
            }}>
              <div style={{
                fontSize: '12px', fontWeight: '600', color: C.textSoft,
                textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '10px',
              }}>
                Statement
              </div>

              <Line label="Revenue" value={r.total} />
              <Line
                sub label="Tape sales" value={r.sales}
                note={`${r.invoice_count || 0} invoice${Number(r.invoice_count) === 1 ? '' : 's'}`}
              />
              <Line
                sub label="Scrap sales" value={r.scrap}
                note={Number(r.scrap_kg) > 0 ? kg(r.scrap_kg) : undefined}
              />

              <Line
                rule label="Cost of goods sold" value={-Number(c.total || 0)}
                note={`${c.rolls_sold || 0} roll${Number(c.rolls_sold) === 1 ? '' : 's'} sold`}
              />

              <Line
                rule bold label="Gross profit" value={pnl.gross_profit} tone={grossTone}
                note={pnl.gross_margin_pct != null ? `${pnl.gross_margin_pct}%` : undefined}
              />

              <Line rule label="Operating expenses" value={-Number(e.total || 0)} />
              {e.by_category.map(cat => (
                <Line
                  key={cat.category_id}
                  sub label={cat.category_name} value={-Number(cat.total)}
                  note={`${cat.entry_count} ${Number(cat.entry_count) === 1 ? 'entry' : 'entries'}`}
                />
              ))}
              {e.by_category.length === 0 && (
                <div style={{
                  paddingLeft: '18px', padding: '7px 0 7px 18px',
                  fontSize: '13px', color: C.textMuted,
                }}>
                  Nothing recorded in this period
                </div>
              )}

              <Line
                rule bold label="Net profit" value={pnl.net_profit} tone={netTone}
                note={pnl.net_margin_pct != null ? `${pnl.net_margin_pct}%` : undefined}
              />

              <p style={{
                margin: '16px 0 0', paddingTop: '12px',
                borderTop: `1px solid ${C.borderSoft}`,
                fontSize: '11px', color: C.textMuted, lineHeight: 1.6,
              }}>
                Revenue is what was invoiced, not what was collected. Cost of goods
                is the traced cost of exactly what was sold, frozen at the moment
                of each invoice — unsold stock is excluded and carried as inventory.
              </p>
            </div>

            {/* Position */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {balances && (
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: '12px', padding: '20px 24px',
                }}>
                  <div style={{
                    fontSize: '12px', fontWeight: '600', color: C.textSoft,
                    textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '10px',
                  }}>
                    Position today
                  </div>

                  <Line label="Receivable from the market" value={balances.receivable} />
                  <Line label="Payable to market"   value={balances.payable} />
                  <Line rule label="Finished stock at cost" value={balances.finished_stock_value} />
                                    <Line label="Raw material at cost" value={balances.raw_stock_value} />
                  <Line label="Cores in WIP at cost" value={balances.wip_stock_value} />

                  <p style={{
                    margin: '14px 0 0', paddingTop: '12px',
                    borderTop: `1px solid ${C.borderSoft}`,
                    fontSize: '11px', color: C.textMuted, lineHeight: 1.6,
                  }}>
                    These are balances as at now, not for the period above. Profit
                    and cash are different questions.
                  </p>
                </div>
              )}

              {/* Trend, custom range only */}
              {mode === 'range' && trend.length > 1 && (
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: '12px', overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '16px 24px 12px',
                    fontSize: '12px', fontWeight: '600', color: C.textSoft,
                    textTransform: 'uppercase', letterSpacing: '0.3px',
                  }}>
                    Month by month
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '420px' }}>
                      <thead>
                        <tr>
                          <th style={{
                            textAlign: 'left', padding: '10px 24px', fontSize: '11px',
                            fontWeight: '600', color: C.textSoft, background: C.rowAlt,
                            textTransform: 'uppercase',
                          }}>Month</th>
                          {['Revenue', 'COGS', 'Expenses', 'Net'].map(h => (
                            <th key={h} style={{
                              textAlign: 'right', padding: '10px 16px', fontSize: '11px',
                              fontWeight: '600', color: C.textSoft, background: C.rowAlt,
                              textTransform: 'uppercase',
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {trend.map(m => {
                          const rev = Number(m.sales_revenue) + Number(m.scrap_revenue);
                          const net = Number(m.net_profit);
                          return (
                            <tr key={m.month_key}>
                              <td style={{
                                padding: '11px 24px', fontSize: '13px', fontWeight: '600',
                                color: C.text, borderTop: `1px solid ${C.borderSoft}`,
                              }}>
                                {m.month_label}
                              </td>
                              <td style={{
                                padding: '11px 16px', fontSize: '13px', textAlign: 'right',
                                color: C.text, borderTop: `1px solid ${C.borderSoft}`,
                              }}>
                                {money(rev)}
                              </td>
                              <td style={{
                                padding: '11px 16px', fontSize: '13px', textAlign: 'right',
                                color: C.textSoft, borderTop: `1px solid ${C.borderSoft}`,
                              }}>
                                {money(m.cogs)}
                              </td>
                              <td style={{
                                padding: '11px 16px', fontSize: '13px', textAlign: 'right',
                                color: C.textSoft, borderTop: `1px solid ${C.borderSoft}`,
                              }}>
                                {money(m.expenses)}
                              </td>
                              <td style={{
                                padding: '11px 16px', fontSize: '13px', textAlign: 'right',
                                fontWeight: '700',
                                color: net >= 0 ? C.success : C.danger,
                                borderTop: `1px solid ${C.borderSoft}`,
                              }}>
                                {money(net)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PnLTab;